/**
 * ENTREGAS Y DEVOLUCIONES LOGÍSTICAS DE MATERIAL (FASE 9.6-B2)
 *
 * Dentro del bloque de cada línea de la OT (sección «Consumos»):
 *   PREVISTO (9.6-B1) · ENTREGADO · CONSUMIDO (9.6-A) · DEVUELTO · PENDIENTE DE JUSTIFICAR
 *
 * Entregar y devolver NO mueven stock: el único movimiento es el consumo
 * validado. Por eso el saldo se llama «Pendiente de justificar» (nunca «stock
 * pendiente»). Consumir más de lo entregado se avisa, no se bloquea; quedar
 * material sin justificar no bloquea el cierre de la OT.
 */
import { useEffect, useState, type ReactNode } from 'react';

import {
  api,
  type AlmacenesConsumoOt,
  type EntregaMaterialOt,
  type EntregasMaterialOt,
  type GrupoLogisticoOt,
  type OpcionArticuloStock,
  type PrevistoOt,
} from '../api.ts';
import { fecha, fechaDeInstante, fechaHora, Modal, useDatos } from '../componentes/base.tsx';
import { Buscador } from '../componentes/buscador.tsx';
import { SelectorLoteOt } from './consumos-ot.tsx';
import { buscarArticulos, erroresDe, ErrorStock, nombreArticulo } from './stock.tsx';
import { codigoAlmacen } from '../componentes/codigo-articulo.tsx';

export const TEXTO_LOGISTICA = 'Registrar una entrega o una devolución no mueve stock: el stock sólo cambia al validar el consumo real.';
export const avisoPendiente = (cantidad: string, unidad: string): string => `Quedan ${cantidad} ${unidad} entregadas pendientes de justificar.`;
export const AVISO_SOBRE_ENTREGADO = 'Se ha consumido más material del registrado como entregado.';

/**
 *  Misma regla que la base (ENTREGA_JUSTIFICA_CONSUMO): sin esta entrega, el grupo ya no cubriría el
 *  consumo validado más lo devuelto. Sólo oculta «Anular»; la base decide.
 */
function justificaConsumo(e: EntregaMaterialOt, grupos: GrupoLogisticoOt[]): boolean {
  const g = grupos.find((x) => x.articulo.id === e.articulo.id && x.almacen.id === e.almacen.id && (x.lote?.id ?? null) === (e.lote?.id ?? null));
  if (g === undefined) return false;
  const c = Number(g.consumidaValidadaStock);
  return c > 0 && c + Number(g.devueltaStock) > Number(g.entregadaStock) - Number(e.cantidadStock);
}

/** Grupo + entregas de UNA línea: resumen logístico, entregas con sus devoluciones y acciones. */
export function EntregasLinea({ idOt, idLinea, datos, previstos, alCambiar }: {
  idOt: string; idLinea: string; datos: EntregasMaterialOt; previstos: PrevistoOt[]; alCambiar: () => void;
}): ReactNode {
  const [dialogo, setDialogo] = useState<null | { t: 'entregar' } | { t: 'devolver' | 'anular'; e: EntregaMaterialOt }>(null);
  const [error, setError] = useState<unknown>(null);
  const [ocupado, setOcupado] = useState(false);
  const [verAnuladas, setVerAnuladas] = useState(false);
  const grupos = datos.grupos.filter((g) => g.idLinea === idLinea);
  const entregas = datos.entregas.filter((e) => e.idLinea === idLinea);
  const activas = entregas.filter((e) => e.estado === 'ACTIVA');
  const anuladas = entregas.filter((e) => e.estado === 'ANULADA');
  if (entregas.length === 0 && !datos.editable) return null;

  async function accion(f: () => Promise<unknown>): Promise<void> {
    setError(null);
    setOcupado(true);
    try { await f(); setDialogo(null); alCambiar(); } catch (e) { setError(e); } finally { setOcupado(false); }
  }

  return (
    <div className="entregas-linea" role="group" aria-label="Entregas de material de la línea">
      <div className="fila" style={{ justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
        <h4 className="pequena titulo-previstos">Entregas de material</h4>
        {datos.editable && <button type="button" className="pequeno" onClick={() => { setError(null); setDialogo({ t: 'entregar' }); }}>Registrar entrega</button>}
      </div>
      {grupos.length > 0 && (
        <ul className="lista-previstos" aria-label="Saldo logístico">
          {grupos.map((g) => <FilaGrupo key={`${g.articulo.id}-${g.almacen.id}-${g.lote?.id ?? ''}`} g={g} />)}
        </ul>
      )}
      {activas.length === 0 ? <p className="suave pequena">Sin entregas registradas.</p> : (
        <ul className="lista-previstos" aria-label="Entregas">
          {activas.map((e) => (
            <FilaEntrega key={e.id} e={e} puedeDevolver={datos.puedeDevolver}
              puedeAnular={datos.editable && e.devoluciones.length === 0 && !justificaConsumo(e, grupos)}
              devolver={() => { setError(null); setDialogo({ t: 'devolver', e }); }} anular={() => { setError(null); setDialogo({ t: 'anular', e }); }} />
          ))}
        </ul>
      )}
      {anuladas.length > 0 && (
        <div className="pequena">
          <button type="button" className="enlace pequeno" aria-expanded={verAnuladas} onClick={() => setVerAnuladas((v) => !v)}>
            {verAnuladas ? 'Ocultar' : 'Ver'} entregas anuladas ({anuladas.length})</button>
          {verAnuladas && (
            <ul className="lista-previstos historico" aria-label="Entregas anuladas">
              {anuladas.map((e) => (
                <li key={e.id} className="fila-previsto retirado"><div><span className="tachado">{e.articulo.descripcion} · {e.cantidad} {e.unidad.codigo}</span>
                  <div className="suave">Anulada por {e.anulada?.por} · {fechaDeInstante(e.anulada?.en ?? null)} · {e.anulada?.motivo}</div></div></li>
              ))}
            </ul>
          )}
        </div>
      )}
      {error !== null && dialogo === null && <ErrorStock error={error} />}
      {dialogo?.t === 'entregar' && (
        <FormEntrega idOt={idOt} previstos={previstos.filter((p) => p.idLinea === idLinea && p.estado === 'ACTIVO')} error={error} ocupado={ocupado}
          cerrar={() => setDialogo(null)}
          enviar={(cuerpo) => void accion(() => api.post(`/api/ots/${idOt}/lineas/${idLinea}/entregas`, cuerpo))} />
      )}
      {dialogo?.t === 'devolver' && (
        <FormDevolucion e={dialogo.e} error={error} ocupado={ocupado} cerrar={() => setDialogo(null)}
          enviar={(cuerpo) => void accion(() => api.post(`/api/ots/${idOt}/entregas/${dialogo.e.id}/devoluciones`, cuerpo))} />
      )}
      {dialogo?.t === 'anular' && (
        <DialogoAnular e={dialogo.e} error={error} ocupado={ocupado} cerrar={() => setDialogo(null)}
          enviar={(motivo) => void accion(() => api.post(`/api/ots/${idOt}/entregas/${dialogo.e.id}/anular`, { motivo, versionEsperada: dialogo.e.version }))} />
      )}
    </div>
  );
}

function FilaGrupo({ g }: { g: GrupoLogisticoOt }): ReactNode {
  const u = g.unidadStock;
  return (
    <li className="fila-previsto grupo-logistico" aria-label={`Saldo de ${g.articulo.codigo}`}>
      <div>
        <strong>{g.articulo.descripcion}</strong> <span className="mono pequena">{g.articulo.codigo}</span>
        <span className="suave pequena"> · {codigoAlmacen(g.almacen)}{g.lote !== null && ` · lote ${g.lote.codigo}`}</span>
        <div className="cifras-previsto pequena">
          <span>Entregado: <strong>{g.entregadaStock} {u}</strong></span>
          <span>Consumido: <strong>{g.consumidaValidadaStock} {u}</strong></span>
          <span>Devuelto: <strong>{g.devueltaStock} {u}</strong></span>
          <span>Pendiente de justificar: <strong>{g.pendienteJustificarStock} {u}</strong></span>
        </div>
        {Number(g.pendienteJustificarStock) > 0 && <div className="nota aviso pequena" role="note">{avisoPendiente(g.pendienteJustificarStock, u)}</div>}
        {Number(g.consumoSobreEntregadoStock) > 0 && <div className="nota info pequena" role="note">{AVISO_SOBRE_ENTREGADO}</div>}
      </div>
    </li>
  );
}

function FilaEntrega({ e, puedeDevolver, puedeAnular, devolver, anular }: {
  e: EntregaMaterialOt; puedeDevolver: boolean; puedeAnular: boolean; devolver: () => void; anular: () => void;
}): ReactNode {
  const sustitucion = e.previsto !== null && e.previsto.idArticulo !== e.articulo.id;
  return (
    <li className="fila-previsto entrega" aria-label={`Entrega de ${e.articulo.codigo}`}>
      <div>
        <strong>{e.articulo.descripcion}</strong> · {e.cantidad} {e.unidad.codigo}
        {e.unidad.id !== e.articulo.unidadStock.id && <span className="suave pequena"> (= {e.cantidadStock} {e.articulo.unidadStock.codigo})</span>}
        <div className="pequena datos-material">
          <span>Almacén {codigoAlmacen(e.almacen)}</span>
          {e.lote !== null && <span>Lote {e.lote.codigo}{e.lote.fechaCaducidad !== null && ` · cad. ${fecha(e.lote.fechaCaducidad)}`}</span>}
          {e.previsto !== null && (sustitucion
            ? <span className="etiqueta info">Previsto {e.previsto.descripcion} → entregado {e.articulo.descripcion}</span>
            : <span>Previsto: {e.previsto.descripcion}</span>)}
          {e.ciclo !== null && <span className="etiqueta info">Reapertura del {fechaDeInstante(e.ciclo.desde)}</span>}
          {Number(e.devueltoStock) > 0 && <span>Devuelto: {e.devueltoStock} {e.articulo.unidadStock.codigo}</span>}
        </div>
        {e.devoluciones.length > 0 && (
          <ul className="lista-devoluciones" aria-label={`Devoluciones de ${e.articulo.codigo}`}>
            {e.devoluciones.map((d) => <li key={d.id} className="pequena">Devuelto {d.cantidad} {d.unidad.codigo} · {d.registradoPor.nombre} · {fechaHora(d.registradoEn)}{d.observaciones !== null && ` · ${d.observaciones}`}</li>)}
          </ul>
        )}
        <div className="suave pequena">Entregado por {e.registradoPor.nombre} · {fechaHora(e.registradoEn)}{e.observaciones !== null && ` · ${e.observaciones}`}</div>
      </div>
      {(puedeDevolver || puedeAnular) && (
        <span className="fila acciones-previsto">
          {puedeDevolver && <button type="button" className="pequeno" disabled={Number(e.maximoDevolvibleStock) <= 0} onClick={devolver}
            title={Number(e.maximoDevolvibleStock) <= 0 ? 'No queda material de esta entrega por justificar' : undefined}>Registrar devolución</button>}
          {puedeAnular && <button type="button" className="pequeno" onClick={anular}>Anular</button>}
        </span>
      )}
    </li>
  );
}

// ============================================================== formularios

/** Opción completa (con trazabilidad y unidades) de un artículo en un almacén, para precargar desde un previsto. */
async function opcionArticulo(idArticulo: string, codigo: string, idAlmacen: string): Promise<OpcionArticuloStock | null> {
  const filas = await buscarArticulos(idAlmacen === '' ? null : idAlmacen)(codigo);
  return filas.find((a) => a.id === idArticulo) ?? null;
}

function FormEntrega({ idOt, previstos, error, ocupado, cerrar, enviar }: {
  idOt: string; previstos: PrevistoOt[]; error: unknown; ocupado: boolean; cerrar: () => void; enviar: (cuerpo: Record<string, unknown>) => void;
}): ReactNode {
  const almacenes = useDatos(() => api.get<AlmacenesConsumoOt>(`/api/ots/${idOt}/consumos/almacenes`), [idOt]);
  const [idPrevisto, setIdPrevisto] = useState('');
  const [idAlmacen, setIdAlmacen] = useState('');
  const [articulo, setArticulo] = useState<OpcionArticuloStock | null>(null);
  const [cantidad, setCantidad] = useState('');
  const [idUnidad, setIdUnidad] = useState('');
  const [idLote, setIdLote] = useState<string | null>(null);
  const [observaciones, setObservaciones] = useState('');
  const campos = erroresDe(error);
  useEffect(() => {
    if (idAlmacen === '' && almacenes.datos?.preseleccion != null) setIdAlmacen(almacenes.datos.preseleccion);
  }, [almacenes.datos]); // eslint-disable-line react-hooks/exhaustive-deps
  //  Elegir un previsto precarga su artículo y cantidad (se pueden cambiar: sustitución logística).
  async function elegirPrevisto(id: string): Promise<void> {
    setIdPrevisto(id);
    const p = previstos.find((x) => x.id === id);
    if (p === undefined) return;
    const a = await opcionArticulo(p.articulo.id, p.articulo.codigo, idAlmacen).catch(() => null);
    if (a !== null) { setArticulo(a); setIdUnidad(p.unidad.id); setIdLote(null); setCantidad(p.cantidad); }
  }
  const serializado = articulo?.trazabilidad?.controlaSeries === true;
  const conLotes = articulo?.trazabilidad?.controlaLotes === true;
  const listo = idAlmacen !== '' && articulo !== null && !serializado && (!conLotes || idLote !== null) && cantidad.trim() !== '';
  const p = previstos.find((x) => x.id === idPrevisto);
  const sinAlmacen = almacenes.datos?.codigo === 'SIN_ALMACEN_CENTRO';
  return (
    <Modal titulo="Registrar entrega de material" cerrar={cerrar}
      pie={<><button type="button" onClick={cerrar}>Cancelar</button>
        <button type="button" className="principal" disabled={!listo || ocupado} onClick={() => enviar({
          idPrevisto: idPrevisto || null, idArticulo: articulo?.id, idAlmacen, idLote: conLotes ? idLote : null,
          idUnidad: idUnidad || articulo?.unidadStock.id, cantidad, observaciones,
        })}>Registrar entrega</button></>}>
      <div onKeyDown={(ev) => { if (ev.key === 'Enter' && (ev.target as HTMLElement).tagName === 'INPUT') ev.preventDefault(); }}>
        <p className="nota info pequena" role="note">{TEXTO_LOGISTICA}</p>
        {error !== null && Object.keys(campos).length === 0 && <ErrorStock error={error} />}
        <div className="form-material">
          {previstos.length > 0 && (
            <div className="campo"><label htmlFor="ent-prev">Previsto relacionado (opcional)</label>
              <select id="ent-prev" value={idPrevisto} onChange={(ev) => void elegirPrevisto(ev.target.value)}>
                <option value="">Material no previsto</option>
                {previstos.map((x) => <option key={x.id} value={x.id}>{x.articulo.descripcion} · {x.cantidad} {x.unidad.codigo}</option>)}
              </select></div>
          )}
          <div className="campo"><label htmlFor="ent-alm">Almacén de origen *</label>
            {sinAlmacen ? <p className="nota aviso pequena" role="status">El centro de esta OT no tiene ningún almacén operativo para ti.</p> : (
              <select id="ent-alm" value={idAlmacen} disabled={almacenes.datos === null}
                onChange={(ev) => { setIdAlmacen(ev.target.value); setIdLote(null); }}>
                <option value="">{almacenes.datos === null ? 'Cargando…' : 'Elige el almacén'}</option>
                {(almacenes.datos?.filas ?? []).map((a) => <option key={a.id} value={a.id}>{codigoAlmacen(a)} · {a.descripcion}{a.tipo === 'MOVIL' ? ' (móvil)' : ''}</option>)}
              </select>
            )}
            {campos['idAlmacen'] !== undefined && <div className="error-campo">{campos['idAlmacen']}</div>}</div>
          <Buscador<OpcionArticuloStock> etiqueta="Artículo *" valor={articulo} contexto={idAlmacen} deshabilitado={idAlmacen === ''}
            placeholder={idAlmacen === '' ? 'Elige antes el almacén' : 'Código o descripción…'}
            buscar={buscarArticulos(idAlmacen === '' ? null : idAlmacen)} clave={(a) => a.id} texto={nombreArticulo}
            detalle={(a) => `Disponible ${a.saldo ?? '0'} ${a.unidadStock.codigo}${a.trazabilidad?.controlaSeries === true ? ' · con nº de serie (no admitido)' : ''}`}
            alElegir={(a) => { setArticulo(a); setIdUnidad(a?.unidadStock.id ?? ''); setIdLote(null); }}
            error={campos['idArticulo']} sinResultados="Ningún artículo activo configurado en este almacén." />
          {p !== undefined && articulo !== null && p.articulo.id !== articulo.id && (
            <p className="nota info pequena" role="note">Previsto {p.articulo.descripcion} → entregado {articulo.descripcion}: quedará como sustitución.</p>
          )}
          {serializado && <p className="nota error pequena" role="alert">Este artículo se controla por número de serie: no se admite como material de Limpieza.</p>}
          {articulo !== null && !serializado && conLotes && idAlmacen !== '' && (
            <SelectorLoteOt idOt={idOt} idArticulo={articulo.id} idAlmacen={idAlmacen} valor={idLote} alElegir={(l) => setIdLote(l.id)}
              unidad={articulo.unidadStock.codigo} error={campos['idLote']} />
          )}
          {articulo !== null && !serializado && (
            <div className="form-rejilla">
              <div className="campo"><label htmlFor="ent-cant">Cantidad *</label>
                <input id="ent-cant" inputMode="decimal" value={cantidad} onChange={(ev) => setCantidad(ev.target.value)} />
                {campos['cantidad'] !== undefined && <div className="error-campo">{campos['cantidad']}</div>}</div>
              <div className="campo"><label htmlFor="ent-ud">Unidad *</label>
                <select id="ent-ud" value={idUnidad} onChange={(ev) => setIdUnidad(ev.target.value)}>
                  {articulo.unidades.map((u) => <option key={u.id} value={u.id}>{u.codigo}{u.factor === '1' ? '' : ` (= ${u.factor} ${articulo.unidadStock.codigo})`}</option>)}
                </select></div>
            </div>
          )}
          <div className="campo"><label htmlFor="ent-obs">Observaciones</label>
            <textarea id="ent-obs" rows={2} maxLength={1000} value={observaciones} onChange={(ev) => setObservaciones(ev.target.value)} /></div>
        </div>
      </div>
    </Modal>
  );
}

function FormDevolucion({ e, error, ocupado, cerrar, enviar }: {
  e: EntregaMaterialOt; error: unknown; ocupado: boolean; cerrar: () => void; enviar: (cuerpo: Record<string, unknown>) => void;
}): ReactNode {
  const [cantidad, setCantidad] = useState('');
  const [idUnidad, setIdUnidad] = useState(e.unidad.id);
  const [motivo, setMotivo] = useState('');
  const campos = erroresDe(error);
  const u = e.articulo.unidadStock.codigo;
  const factor = Number(e.articulo.unidades.find((x) => x.id === idUnidad)?.factor ?? '1');
  const enStock = Math.round(Number(cantidad.replace(',', '.')) * factor * 1e6) / 1e6;
  const excede = cantidad.trim() !== '' && enStock > Number(e.maximoDevolvibleStock);
  return (
    <Modal titulo="Registrar devolución" cerrar={cerrar}
      pie={<><button type="button" onClick={cerrar}>Cancelar</button>
        <button type="button" className="principal" disabled={cantidad.trim() === '' || excede || ocupado}
          onClick={() => enviar({ cantidad, idUnidad, observaciones: motivo })}>Registrar devolución</button></>}>
      {error !== null && Object.keys(campos).length === 0 && <ErrorStock error={error} />}
      <p><strong>{e.articulo.descripcion}</strong> · {codigoAlmacen(e.almacen)}{e.lote !== null && ` · lote ${e.lote.codigo}`}</p>
      <dl className="cifras-devolucion" aria-label="Saldo de la entrega">
        <div><dt>Entregado</dt><dd>{e.cantidadStock} {u}</dd></div>
        <div><dt>Consumido</dt><dd>{e.consumidoGrupoStock} {u}</dd></div>
        <div><dt>Ya devuelto</dt><dd>{e.devueltoStock} {u}</dd></div>
        <div><dt>Máximo devolvible</dt><dd><strong>{e.maximoDevolvibleStock} {u}</strong></dd></div>
      </dl>
      <p className="nota info pequena" role="note">Vuelve al mismo almacén y lote de la entrega. {TEXTO_LOGISTICA}</p>
      <div className="form-rejilla">
        <div className="campo"><label htmlFor="dev-cant">Cantidad *</label>
          <input id="dev-cant" inputMode="decimal" value={cantidad} onChange={(ev) => setCantidad(ev.target.value)} />
          {excede && <div className="error-campo">No puede superar el máximo devolvible ({e.maximoDevolvibleStock} {u}).</div>}
          {campos['cantidad'] !== undefined && <div className="error-campo">{campos['cantidad']}</div>}</div>
        <div className="campo"><label htmlFor="dev-ud">Unidad *</label>
          <select id="dev-ud" value={idUnidad} onChange={(ev) => setIdUnidad(ev.target.value)}>
            {e.articulo.unidades.map((x) => <option key={x.id} value={x.id}>{x.codigo}</option>)}
          </select></div>
      </div>
      <div className="campo"><label htmlFor="dev-mot">Motivo / observaciones</label>
        <input id="dev-mot" maxLength={1000} value={motivo} onChange={(ev) => setMotivo(ev.target.value)} /></div>
    </Modal>
  );
}

function DialogoAnular({ e, error, ocupado, cerrar, enviar }: { e: EntregaMaterialOt; error: unknown; ocupado: boolean; cerrar: () => void; enviar: (motivo: string) => void }): ReactNode {
  const [motivo, setMotivo] = useState('');
  return (
    <Modal titulo="Anular entrega" cerrar={cerrar}
      pie={<><button type="button" onClick={cerrar}>Volver</button>
        <button type="button" className="principal" disabled={motivo.trim().length < 5 || ocupado} onClick={() => enviar(motivo.trim())}>Anular entrega</button></>}>
      {error !== null && <ErrorStock error={error} />}
      <p><strong>{e.articulo.descripcion}</strong> · {e.cantidad} {e.unidad.codigo}</p>
      <p className="suave pequena">Sólo para un registro erróneo: no se anula si ya tiene devoluciones o si justifica un consumo validado.</p>
      <div className="campo"><label htmlFor="anu-mot">Motivo (obligatorio)</label>
        <input id="anu-mot" maxLength={2000} value={motivo} onChange={(ev) => setMotivo(ev.target.value)} /></div>
    </Modal>
  );
}
